
<style scoped>
    .swiper-pagination-bullet .swiper-pagination-bullet-active{
        background-color: #0a543f !important;
    }
</style>

<template>
    <layout-general-container spaceToolbar>
        <div class="w-full flex justify-center">
            <div class="w-full flex flex-col gap-20">
                <div
                    class="bg-banner-1 h-[40vh] border-b px-6 flex flex-col gap-8 items-center justify-center text-center w-full">
                    <h1 class="text-white text-4xl font-semibold tracking-wide">Our Graduates</h1>
                    <h2 class="text-white tracking-wide leading-loose md:max-w-6xl font-light">
                        Meet our accomplished graduates who have transformed their business through our programs.
                    </h2>
                </div>
                <div class="w-full flex-col flex gap-8 bg-white">
                    <h2 class="px-6 md:px-8 xl:px-14 text-2xl md:text-3xl text-primary tracking-wide font-semibold">Meet our accomplished graduates who have transformed their business through our programs.
                    </h2>

                    <div class="relative px-14 xl:px-32">
                        <Swiper
                            :modules="[Pagination, Navigation, Autoplay]"
                            :slides-per-view="1"
                            :space-between="20"
                            :loop="true"
                            :autoplay="{ delay: 3000 }"
                            :breakpoints="{
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 }
                            }"
                            :navigation="{
                            nextEl: '.custom-next',
                            prevEl: '.custom-prev'
                            }"
                            :pagination="{
                                el: '.custom-pagination',
                                clickable: true,
                                renderBullet: function (index, className) {
                                    return `<span class='`+className+` rounded-none bg-primary w-3 h-1'></span>`;
                                },
                            }"
                            class="mySwiper"
                        >
                            <SwiperSlide v-for="item in companies" :key="item.name">
                                <div class="p-6 border bg-white rounded-md shadow-sm">
                                    <img :src="item.image" class="w-16 h-16 rounded-full object-cover mb-2" />
                                    <div class="text-base font-semibold">{{ item.name }}</div>
                                    <div class="text-sm text-gray-600">{{ item.position }}</div>
                                    <p class="text-sm mt-2">{{ item.description.slice(0, 120) }}...</p>
                                </div>
                            </SwiperSlide>
                        </Swiper>

                        <div class="absolute inset-0 flex justify-between items-center pointer-events-none px-3 xl:px-14">
                            <button class="custom-prev z-10 text-2xl font-bold text-gray-700 pointer-events-auto bg-white p-2 rounded-full shadow">
                                <IconChevronLeft class="text-primary" />
                            </button>
                            <button class="custom-next z-10 text-2xl font-bold text-gray-700 pointer-events-auto bg-white p-2 rounded-full shadow">
                                <IconChevronRight class="text-primary" />
                            </button>
                        </div>

                    </div>
                    <div class="custom-pagination flex justify-center gap-2"></div>
                </div>

                <div class="w-full mt-10 px-6 xl:px-0 flex flex-col gap-24 items-center justify-center" id="graduates-news">
                    <div class="grid grid-cols-1 md:grid-cols-2 items-center w-full md:max-w-6xl gap-3">
                        <h2 class="text-primary leading-loose text-xl tracking-wide md:text-4xl font-semibold">
                            Our Latest <br> <span class="text-accent">Events</span>
                        </h2>
                        <p class="text-base leading-loose tracking-wide md:leading-loose text-light">
                            Follow our impactful stories! Here are some of our top-picked articles and press publications
                            for you to read. Learn more about what we are up to and join us in this journey
                        </p>
                    </div>
                    <div class="grid grid-cols-1 xl:grid-cols-3 w-full md:max-w-6xl gap-8" v-if="events?.data?.length > 0 && !isLoading">
                        <div v-for="item in events.data" :key="item.title"
                            class="bg-white border items-center w-full flex font-sans flex-col rounded-md gap-6">
                            <div class="flex cursor-pointer flex-col w-full">
                                <div class="pb-3 flex flex-row w-full justify-between items-center">
                                    <img class="w-full min-h-[12rem] h-[12rem] object-cover" :src="item.featured_image" :alt="item.title">
                                </div>
                                <div class="flex flex-col gap-3 px-5 pb-6">
                                    <p class="text-lg font-medium text-primary leading-loose">{{ item.title.length > 90 ?
                                        item.title.slice(0, 90) : item.title }}....</p>
                                    <div class="text-sm text-secondary leading-loose line-clamp-4" v-if="item?.short_desc">{{ item?.short_desc }}</div>
                                    <div class="text-sm text-secondary leading-loose line-clamp-4" v-else v-html="sanitizedDesc(item.content)"></div>

                                    <a-button @click="goTo('/Event/Detail/' + item.slug)" type="primary"
                                        class="flex justify-center w-fit flex-row gap-1 items-center self-end"  :id="'graduates-read-more-'+item.title?.toLowerCase()?.replace(/ /g, '-').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '')">
                                        Read More
                                    </a-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="!isLoading" class="mx-auto w-full mt-4">
                        <p class="text-center text-gray-500 text-lg font-semibold w-full">
                            No Latest Event
                        </p>
                    </div>
                    
                    <a-pagination 
                        class="self-start w-full md:max-w-6xl mx-auto"
                        v-model:current="page" 
                        :total="events.total_items"
                        :page-size="events.per_page"
                        :show-total="showTotal"
                        :id="'graduates-pagination'"
                    />

                </div>
            </div>
        </div>
    </layout-general-container>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import DOMPurify from 'dompurify';
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import { Pagination, Navigation, Autoplay } from 'swiper/modules'

import Graduates1 from "@/assets/img/graduates/1.jpeg"
import Graduates2 from "@/assets/img/graduates/2.jpeg"
import Graduates3 from "@/assets/img/graduates/3.png"
import Graduates4 from "@/assets/img/graduates/4.jpeg"
import Graduates5 from "@/assets/img/graduates/5.jpeg"
import Graduates6 from "@/assets/img/graduates/6.png"

import GraduatesNews1 from "@/assets/img/graduates/20240912_frontday-Java-fresh_140-1.jpg"
import GraduatesNews2 from "@/assets/img/graduates/WhatsApp-Image-2024-09-19-at-15.05.20_08cf6a00.jpg"
import GraduatesNews3 from "@/assets/img/graduates/WhatsApp-Image-2024-09-06-at-14.27.40_5d4d49d2.jpg"
import GraduatesNews4 from "@/assets/img/graduates/WhatsApp-Image-2024-07-26-at-16.53.31_e9100818.jpg"
import GraduatesNews5 from "@/assets/img/graduates/WhatsApp-Image-2024-08-21-at-18.39.49_c7bde700.jpg"
import GraduatesNews6 from "@/assets/img/graduates/IMG-20240715-WA0019-1.jpg"
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-vue'

import { useRouter } from 'vue-router'
import axios from "@/plugins/axios";


const router = useRouter();

const page = ref(1);
const events = ref({});
const isLoading = ref(true);

const sanitizedDesc = (desc) => {
    return DOMPurify.sanitize(desc);
}

const fetchEvents = async () => {
    try {
        const tag = router.currentRoute.value.query.tag || null;

        const response = await axios.get('articles', {
            params: {
                page: page.value,
                tag: tag,
            }
        });

        events.value = response.data;
    } catch (error) {
        console.error('Error fetching events:', error);
    } finally {
        isLoading.value = false;
    }
};

// const events = ref([
//     {
//         title: 'Front-Day Sustainability: Harvesting Connections between IKEA Co-workers and Java Fresh',
//         description: 'Recently, IKEA co-workers had the chance to dive into the world of social entrepreneurship in Front-Day Sustainability, an initiative designed to connect IKEA co-workers with social entrepreneurs participating in Instellar & IKEA Social Entrepreneurship Accelerator (I-SEA). This two-day initiative brought IKEA co-workers to visit Java Fresh’s work sites in Tasikmalaya,  where they could actively contribute to the social enterprise by providing support and insights to Java Fresh workers and get a firsthand look into social enterprise in action.',
//         image: GraduatesNews1,
//         // link: 'https://instellar.id/front-day-sustainability-harvesting-connections-between-ikea-co-workers-and-java-fresh/'
//     },
//     {
//         title: 'Building Collaborative and Inclusive Leadership at I-SEA Cohort 2 Leadership Training',
//         description: 'The SDG Academy room at Tanoto Foundation was filled with excitement as Changemakers from the 10 selected social enterprises in the I-SEA program underwent a two-day leadership training on 4th-5th September 2024. The training helped them to identify their personal leadership styles, build a collaborative work culture, and become inclusive leaders in their social enterprises. Facilitated by Social Entrepreneurship Academy, IKEA Indonesia, and Instellar, the training provided an interactive atmosphere with discussions and sharing sessions that encouraged participants to support each other for a greater impact progress.',
//         image: GraduatesNews2,
//         // link: 'https://instellar.id/building-collaborative-and-inclusive-leadership-at-i-sea-cohort-2-leadership-training/'
//     },
//     {
//         title: 'Six-Month Journey of I-SEA Changemakers: Transformation, Innovation, and Positive Impact',
//         description: 'It’s hard to believe that the second cohort of the I-SEA program has already been running for more than six months. In this short period, the I-SEA Changemakers have gone through extensive mentoring to develop their businesses and amplify their impact. With guidance, mentorship, funding, and tailored ecosystem support from the I-SEA program, they have achieved remarkable milestones worth celebrating.',
//         image: GraduatesNews3,
//         // link: 'https://instellar.id/six-month-journey-of-i-sea-changemakers-transformation-innovation-and-positive-impact/'
//     },
//     {
//         title: 'Deep Impact Discovery Trip: Connecting Korean Innovators with Indonesian Business Landscape',
//         description: 'The Indonesian market continues to attract significant attention from investors in the technology industry, indicating substantial potential within the global business ecosystem. This interest has also attracted the KT&G Startup Sangsang Camp, a startup incubator program from Korea, organized by the KT&G—a leading global company from South Korea specializing in tobacco and ginseng production. The entire journey in Indonesia is encapsulated in the Deep Impact Discovery Trip, a visit designed to empower young Korean social innovators to understand the Indonesian market and establish collaborations and partnerships with young Indonesian social innovators. Over four days, from July 23-26, they will visit and meet directly with startup entrepreneurs, investors, and other industry stakeholders in Indonesia. This event is facilitated by Merry Year Social Company (MYCS) from Korea and Instellar. ',
//         image: GraduatesNews4,
//         // link: 'https://instellar.id/deep-impact-discovery-trip-connecting-korean-innovators-with-indonesian-business-landscape/'
//     },
//     {
//         title: 'Five Green Startups in Bali Embark Transformative Journey  with HATCH x PEDE Incubation Programme',
//         description: 'The HATCH x PEDE Incubation Programme for Bali has officially kicked off with the Onboarding Programme which was held on August 15-16, 2024. This event marked the beginning of a transformative journey for five innovative green startups: Daurulang.id, Umah Pupa, Electric Wheel, Biojel, and Patala. Over the course of two days, our new Bali cohort of 5 green innovators gathered together to lay the groundwork for the activities that lie ahead in the three-month incubation programme. During this time, they will explore their business ideas, uncovering collaboration opportunities, and addressing challenges head-on. With a series of workshops and mentorship sessions in this incubation programme, they will build skills in strategic planning, operational execution, and growth hacking, all aimed at scaling up their business game to a higher scale and impact.',
//         image: GraduatesNews5,
//         // link: 'https://instellar.id/five-green-startups-in-bali-embark-transformative-journey-with-hatch-x-pede-incubation-programme/'
//     },
//     {
//         title: 'The Importance of Hands-on Support in the I-SEA Programme',
//         description: 'For most social entrepreneurs, challenges often beset them with respect to limited resources and access to mentorship, putting pressure to scale for more societal benefit. In these battles, supportive programmes like the Instellar & IKEA Social Entrepreneurship Accelerator (I-SEA) become indispensable. It is because of this strong framework of support and guidance that I-SEA shall not only uplift these entrepreneurs, but their potential to be a force of impactful change is also increased.',
//         image: GraduatesNews6,
//         // link: 'https://instellar.id/the-importance-of-hands-on-support-in-the-i-sea-programme/'
//     }
// ])

const companies = ref([
    {
        name: "Avani",
        image: Graduates1,
        description: "Providing a full range of sustainable packaging and hospitality products made from renewable and natural ingredients that are fully compostable.",
        position: "Founder & CEO of Aviani",
        program: "I-SEA Graduate"

    },
    {
        name: "Agradaya",
        image: Graduates2,
        description: "Social enterprise that focused on natural spices products and partnerships with local farmers located in Yogyakarta and East Java.",
        position: "Founder & CEO of Agradaya",
        program: "DBS Foundation Bootcamp 2021 Graduate"
    },
    {
        name: "Campa",
        image: Graduates3,
        description: "Campa is an social enterprise specializing in tour travel service in Indonesia. In Campa, customers will explore more on the history and culture of unique destinations and experience interacting with locals",
        position: "Co-Founder & CEO of Campa",
        program: "MIH EAST Graduate"
    },
    {
        name: "Rahsa.id",
        image: Graduates4,
        description: "Rahsa.id presents and supports brands and products that are #SehatLokalBeragam, meaning those who stand for nature and locality, are fair to others, are also diverse and have an impact on our daily lives according to their benefits.",
        position: "Founder & CEO of Rahsa.id",
        program: "I-SEA Graduate"
    },
    {
        name: "Shipper",
        image: Graduates5,
        description: "Shipper is one of the logistics & warehouse aggregators in Indonesia that provides reliable freight forwarding and warehouse rental services.",
        position: "Founder & CEO of Shipper",
        program: "I-SEA Graduate"
    },
    {
        name: "Nichoa",
        image: Graduates6,
        description: "Nichoa has been committed to not only producing high-quality chocolate, but also to improving the lives of local cocoa farmers and consumers since its inception in 2018.",
        position: "Founder & CEO of Nichoa",
        program: "I-SEA Graduate"
    }
]);

const showTotal = () => {
  return `Total ${events?.value?.total_items} items`
}

onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('tag')) {
        const section = document.getElementById('graduates-news');
        if (section) {
            const isMobile = window.innerWidth < 768;
            const yOffset = isMobile ? -80 : -96;
            const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    }

    fetchEvents();
});

watch(page, () => {
    fetchEvents();
});

const goTo = (url) => {
    window.location.href = url;
};
</script>
